import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { errorMsg } from "../../../Apis/functions";
import ButtonIcon from "../../../components/buttons/ButtonIcon";
import { CleanIcon } from "../../../components/buttons/IconButtons";
import { CreateResident } from "../../../reduxToolkit/Building/BuildingApi";
import { RELATIONSHIP_TYPE_CHIP } from "../../Building/Model/Model";
import { MY_HOUSEHOLD_MEMBER_MODEL, MY_RELATIONSHIP_TYPES } from "../Model/Model";

/**
 * @Author: karen Yunuen Pozos Arias
 * @JobTitle : Full Stack Developer
 * @Email : kpozos@ibl.mx
 *
 * Add household member modal logic: the form model, its validation and the
 * handlers the modal binds to the inputs and to the footer buttons.
 *
 * @param {Object} props
 * @param {boolean} props.show - Modal visibility, the form is reset every time it changes.
 * @param {Function} props.onHide - Closes the modal.
 * @param {Function} [props.setModified] - Flags the members list to be reloaded after a successful creation.
 * @param {Object} [props.buildingDetail] - Building of the logged resident, the member is registered on it.
 * @returns {Object} Localization function, form state and the modal handlers.
 */
export const CreateHouseholdMemberModalApp = ({ show, onHide = () => {}, setModified, buildingDetail } = {}) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const [emptyFields, setEmptyFields] = useState(false);
    const [member, setMember] = useState({ ...MY_HOUSEHOLD_MEMBER_MODEL });
    const [relationshipList, setRelationshipList] = useState([]);

    // TODO: options of the relationship autocomplete, they come from the household
    //       relationships while building-service does not expose the ones already
    //       registered
    const relationshipOptions = useMemo(
        () => MY_RELATIONSHIP_TYPES.map((type) => ({
            id: type,
            label: t(RELATIONSHIP_TYPE_CHIP[type]?.translation)
        })),
        [t]
    );

    useEffect(() => {
        reset();
    }, [show]);

    /**
     * Leaves the form as it is opened, every field empty and no relationship
     * picked.
     */
    const reset = () => {
        setEmptyFields(false);
        setMember({ ...MY_HOUSEHOLD_MEMBER_MODEL });
        setRelationshipList([]);
    };

    /**
     * Clear button of the modal title, it empties the form.
     *
     * @returns {JSX.Element}
     */
    const clearButton = () => {
        return (
            <ButtonIcon
                id={'btn_chm_clean'}
                Icon={CleanIcon}
                onClick={reset}
                title={'clear_instances'}
                className={"btn-clean-secondary-600"}
                tooltipPlacement={"right"}
                iconProps='btn-icon-svg-12px icon-color-general-00'
            />
        )
    };

    /**
     * Updates the field the resident is typing on.
     *
     * @param {Object} e - Input change event.
     */
    const handleChange = (e) => {
        const { name, value } = e.target;

        setMember((prev) => ({ ...prev, [name]: value }));
    };

    /**
     * Keeps the relationship picked on the chip, the field only holds one so the
     * member stores the RELATIONSHIP_TYPE value of the chip standing, the card
     * is the one that translates it.
     *
     * @param {Array<Object>} [list] - Chips of the field, empty when the one picked is removed.
     */
    const handleSelectRelationship = (list) => {
        const selected = list || [];

        setRelationshipList(selected);
        setMember((prev) => ({ ...prev, relationship: selected[0]?.id || '' }));
    };

    /**
     * Closes the modal and leaves the form empty.
     */
    const handleClose = () => {
        reset();
        onHide();
    };

    /**
     * Validates the form and registers the household member on the apartment of
     * the logged resident, who is the one that gives them the access.
     */
    const handleSave = () => {
        if (!member?.isValid()) {
            setEmptyFields(true);
            return;
        }
        setEmptyFields(false);

        const body = member.toRegister(
            buildingDetail?.buildingApartmentId,
            buildingDetail?.residentId
        );

        dispatch(CreateResident(body)).then(({ payload: { data: { success, code } } }) => {
            if (success) {
                toast.success(t('has_been_added_successfully'));
                // Reloads the list of the tab with the member just registered
                setModified?.(true);
                handleClose();
            } else
                errorMsg(code);
        });
    };

    return {
        t,
        emptyFields,
        member,
        relationshipList,
        relationshipOptions,
        clearButton,
        handleChange,
        handleSelectRelationship,
        handleClose,
        handleSave
    };
};
